import React, { useEffect, useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';

// Constants for capacity calculations
const LINE_HEIGHT_MM = 5.5; // Approximate line height in mm (13px font * 1.5 line-height ≈ 5.5mm)

/**
 * TiptapEditor - A modern rich text editor using Tiptap (ProseMirror-based)
 * 
 * Props:
 * - value: HTML string content
 * - onChange: Callback when content changes
 * - placeholder: Placeholder text when empty
 * - initialHeight: Minimum height in mm (default: 30)
 * - style: Additional styles for the container
 * - pageContext: Context info (editorId, maxHeight, etc.)
 * - onContentOverflow: Callback when content height changes
 * - onToolbarCommand: Ref for toolbar command handling
 * - onFocusChange: Callback when focus state changes
 */
const TiptapEditor = ({
  value,
  onChange,
  placeholder = '',
  initialHeight = 30,
  style = {},
  pageContext = {},
  onContentOverflow,
  onToolbarCommand: onToolbarCommandRef,
  onFocusChange
}) => {
  // Track focus state internally
  const [isFocused, setIsFocused] = useState(false);
  // Track content metrics for overflow detection
  const [contentMetrics, setContentMetrics] = useState({
    currentHeight: 0,
    remainingLines: null,
    usagePercent: 0,
    isOverflowing: false
  });

  // Initialize editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure starter kit options
        heading: false, // Disable headings for report content
        codeBlock: false, // Disable code blocks
        blockquote: false, // Disable blockquotes
      }),
      Placeholder.configure({
        placeholder: placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
        spellcheck: 'true',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Don't send empty paragraph as content
      const cleanHtml = html === '<p></p>' ? '' : html;
      onChange(cleanHtml);
      
      // Measure and report height
      measureHeight();
    },
    onFocus: () => {
      setIsFocused(true);
      if (onFocusChange) {
        onFocusChange(true);
      }
    },
    onBlur: ({ event }) => {
      // Check if focus moved to toolbar
      const relatedTarget = event?.relatedTarget;
      const isToolbarClick = relatedTarget?.closest?.('.rich-text-toolbar');
      
      if (!isToolbarClick) {
        setIsFocused(false);
        if (onFocusChange) {
          onFocusChange(false);
        }
      }
    },
  });

  // Measure content height and report to parent
  const measureHeight = useCallback(() => {
    // Guard: ensure editor and view are fully initialized
    if (!editor || !editor.view || !editor.view.dom) return;
    
    try {
      const editorElement = editor.view.dom;
      if (!editorElement) return;
      
      // Get natural height
      const scrollHeight = editorElement.scrollHeight;
      const mmToPx = 3.779527559;
      const heightInMm = scrollHeight / mmToPx;
      
      // Report height (minimum of initialHeight)
      const contentHeight = Math.max(heightInMm, initialHeight);
      const maxHeight = pageContext.maxHeight;
      const needsSplit = maxHeight && contentHeight > maxHeight;
      
      // Calculate capacity metrics
      if (maxHeight) {
        const remainingHeightMm = Math.max(0, maxHeight - contentHeight);
        const remainingLines = Math.floor(remainingHeightMm / LINE_HEIGHT_MM);
        const usagePercent = Math.min(100, Math.round((contentHeight / maxHeight) * 100));
        const isOverflowing = contentHeight > maxHeight;
        
        setContentMetrics({
          currentHeight: contentHeight,
          remainingLines: remainingLines,
          usagePercent: usagePercent,
          isOverflowing: isOverflowing
        });
      }
      
      // Report to parent if callback exists
      if (onContentOverflow) {
        onContentOverflow({
          editorId: pageContext.editorId,
          contentHeight: contentHeight,
          content: editor.getHTML(),
          needsSplit: needsSplit,
          maxHeight: maxHeight
        });
      }
    } catch (e) {
      // Silently ignore errors if editor view is not ready
      console.debug('TiptapEditor: measureHeight called before editor ready');
    }
  }, [editor, initialHeight, pageContext, onContentOverflow]);

  // Sync external value changes to editor
  useEffect(() => {
    // Guard: ensure editor is fully initialized before accessing
    if (!editor || !editor.view) return;
    
    if (value !== undefined) {
      try {
        const currentContent = editor.getHTML();
        const normalizedCurrent = currentContent === '<p></p>' ? '' : currentContent;
        const normalizedValue = value || '';
        
        // Only update if content actually differs
        if (normalizedCurrent !== normalizedValue) {
          // Preserve cursor position when possible
          editor.commands.setContent(normalizedValue, false);
          
          // Measure height after content update (with small delay for DOM to update)
          setTimeout(measureHeight, 50);
        }
      } catch (e) {
        // Silently ignore errors if editor is not ready
        console.debug('TiptapEditor: sync value called before editor ready');
      }
    }
  }, [value, editor, measureHeight]);

  // Initial height measurement after mount
  useEffect(() => {
    if (editor) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(measureHeight, 100);
      return () => clearTimeout(timer);
    }
  }, [editor, measureHeight]);

  // Handle toolbar commands - only when this editor is focused
  useEffect(() => {
    if (onToolbarCommandRef && editor && isFocused) {
      onToolbarCommandRef.current = (command, commandValue) => {
        console.log('🎯 TiptapEditor received command:', command);
        
        // Focus the editor if not already focused
        if (!editor.isFocused) {
          editor.commands.focus();
        }
        
        switch (command) {
          case 'bold':
            editor.chain().focus().toggleBold().run();
            console.log('✅ Bold toggled');
            break;
          case 'italic':
            editor.chain().focus().toggleItalic().run();
            console.log('✅ Italic toggled');
            break;
          case 'underline':
            // Underline not in StarterKit by default
            // Could add @tiptap/extension-underline if needed
            console.log('⚠️ Underline not supported yet');
            break;
          case 'insertUnorderedList':
            editor.chain().focus().toggleBulletList().run();
            console.log('✅ Bullet list toggled');
            break;
          case 'insertOrderedList':
            editor.chain().focus().toggleOrderedList().run();
            console.log('✅ Ordered list toggled');
            break;
          case 'removeFormat':
            editor.chain().focus().unsetAllMarks().clearNodes().run();
            console.log('✅ Format removed');
            break;
          case 'undo':
            editor.chain().focus().undo().run();
            console.log('✅ Undo');
            break;
          case 'redo':
            editor.chain().focus().redo().run();
            console.log('✅ Redo');
            break;
          default:
            console.log('Unknown command:', command);
        }
      };
    }
  }, [editor, onToolbarCommandRef, isFocused]);

  // Note: Editor cleanup is handled by @tiptap/react internally
  // No need for manual cleanup useEffect

  if (!editor) {
    return null;
  }

  // Simple check for overflow - just use red border
  const isOverflowing = contentMetrics.isOverflowing;

  return (
    <div
      className={`tiptap-editor-wrapper ${isOverflowing ? 'overflow-warning' : ''}`}
      data-overflow={isOverflowing ? 'true' : 'false'}
      data-editor-id={pageContext.editorId || ''}
      style={{
        ...style,
        minHeight: `${initialHeight}mm`,
        border: `2px solid ${isOverflowing ? '#dc2626' : '#d1d5db'}`,
        borderRadius: '2px',
        backgroundColor: 'white',
        overflow: 'visible',
        transition: 'border-color 0.2s',
        ...(editor.isFocused && !isOverflowing && {
          borderColor: '#3b82f6',
          boxShadow: '0 0 0 1px #3b82f6'
        }),
        ...(editor.isFocused && isOverflowing && {
          borderColor: '#dc2626',
          boxShadow: '0 0 0 1px #dc2626'
        })
      }}
    >
      <EditorContent 
        editor={editor} 
        className="tiptap-editor"
      />
      
      <style>{`
        .tiptap-editor-wrapper {
          position: relative;
        }
        
        .tiptap-editor .ProseMirror {
          padding: 12px 16px;
          font-size: 13px;
          line-height: 1.5;
          font-family: inherit;
          outline: none;
          min-height: inherit;
        }
        
        .tiptap-editor .ProseMirror p {
          margin: 0 0 0.5em 0;
        }
        
        .tiptap-editor .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        
        /* Placeholder styling */
        .tiptap-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          font-style: italic;
          pointer-events: none;
          height: 0;
        }
        
        .tiptap-editor .ProseMirror.is-editor-empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          font-style: italic;
          pointer-events: none;
          position: absolute;
          top: 12px;
          left: 16px;
        }
        
        /* Formatting styles */
        .tiptap-editor .ProseMirror strong {
          font-weight: 700;
        }
        
        .tiptap-editor .ProseMirror em {
          font-style: italic;
        }
        
        /* List styles */
        .tiptap-editor .ProseMirror ul,
        .tiptap-editor .ProseMirror ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
        
        .tiptap-editor .ProseMirror ul {
          list-style-type: disc;
        }
        
        .tiptap-editor .ProseMirror ol {
          list-style-type: decimal;
        }
        
        .tiptap-editor .ProseMirror li {
          margin: 0.25em 0;
        }
        
        .tiptap-editor .ProseMirror li p {
          margin: 0;
        }
        
        /* Nested lists */
        .tiptap-editor .ProseMirror ul ul,
        .tiptap-editor .ProseMirror ol ol,
        .tiptap-editor .ProseMirror ul ol,
        .tiptap-editor .ProseMirror ol ul {
          margin: 0.25em 0;
        }
      `}</style>
    </div>
  );
};

export default TiptapEditor;
